// @flow
import React from 'react'
import glamorous from 'glamorous'
import { css } from 'glamor'
import { CSSTransitionGroup } from 'react-transition-group'
import FocusLock from 'react-focus-lock'
import content from 'pp-react-l10n'
import {
  freezeMainContent,
  unfreezeMainContent,
  setAppElement as _setAppElement,
} from './utils'
import { mediumAndSmaller } from '../../lib/styles/media-queries'
import Portal from './portal'
import { connect } from '../../transfer/utils/p2p-connect'
import * as OverpanelActions from '../../transfer/actions/overpanels'
import { trackLink } from 'lib/analytics/fpti'
import { getFlowType } from '../../transfer/utils/url-utils'

let i18n = content('inc/overpanel')

export const DEFAULT_DURATION = 400

const PRESET = {
  GLIMPSE: 'GLIMPSE',
  FULL: 'FULL',
}
const fadeEnter = css({ opacity: 0 })
const fadeEnterActive = css({
  opacity: 1,
  transition: `opacity ${DEFAULT_DURATION}ms ease-in-out`,
})
const fadeLeave = css({ opacity: 1 })
const fadeLeaveActive = css({
  opacity: 0,
  transition: `opacity ${DEFAULT_DURATION}ms ease-in-out`,
})

const slideEnter = css({
  // fixed while animating to prevent overpanel jumping issue due to overflow
  position: 'fixed !important',
  transform: 'translateY(100%)',
})
const slideEnterActive = css({
  // fixed while animating to prevent overpanel jumping issue due to overflow
  position: 'fixed !important',
  transform: 'translateY(0)',
  transition: `transform ${DEFAULT_DURATION}ms ease-out`,
})
const slideLeave = css({ transform: 'translateY(0)' })
const slideLeaveActive = css({
  transform: 'translateY(100%)',
  transition: `transform ${DEFAULT_DURATION}ms ease-in`,
})

const Background = glamorous.div({
  background: 'rgba(210, 210, 210, .5)',
  position: 'fixed',
  zIndex: 1000,
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
})
const Wrapper = glamorous.div({
  position: 'absolute', // use absolute to fix issue with vertical scrolling in iOS
  right: 0,
  left: 0,
  top: 0,
  bottom: 0,
  zIndex: 1050,
  overflow: 'auto',
})

const Content = glamorous.div(
  'col-xs-12',
  {
    minHeight: '100%',
    boxSizing: 'border-box',
    padding: '70px 50px',
    backgroundColor: 'white',
    [mediumAndSmaller]: {
      padding: '80px 30px',
    },
    ':focus': {
      outline: 'none',
    },
  },
  ({ preset }) =>
    preset === PRESET.GLIMPSE
      ? 'col-sm-8 col-sm-offset-2 col-lg-6 col-lg-offset-3'
      : ''
)

const Dialog = glamorous.div({
  ':focus': {
    outline: 'none',
  },
})

const CloseButtonContainer = glamorous.button({
  position: 'absolute',
  top: '0',
  right: '0',
  padding: '15px',
  margin: '10px',
  zIndex: 100,
  background: 'none',
  border: 0,
})

type CloseButtonProps = {
  onClick: () => any,
  hideCloseButton?: boolean,
  css?: {},
}
function CloseButton(props: CloseButtonProps) {
  const { hideCloseButton } = props
  if (hideCloseButton) return null
  return (
    <CloseButtonContainer {...props}>
      <span className="icon icon-close-small" aria-hidden="true" />
      <span className="accessAid">{i18n('button.close')}</span>
    </CloseButtonContainer>
  )
}
export type OverpanelProps = {
  show: boolean,
  handleClose: () => {},
  onClosed?: () => {},
  onOverpanelOpen?: () => {},
  // TODO: make it required once we have all accessibility content
  a11yTitle?: string,
  /* preset overpanel style
  FULL makes overpanel a full page
  GLIMPSE makes overpanel a centered panel */
  preset?: 'GLIMPSE' | 'FULL',
  shouldCloseOnEsc?: boolean,
  styleOverrides: {
    animationDuration?: number,
    backgroundAnimation?: {},
    background?: {},
    contentAnimation?: {},
    content?: {},
    closeButton?: {},
  },
  // TODO: make them required once we provide these analytics to all overpanels
  pageName?: string,
  pageGroup?: string,
  dispatch: ({}) => {},
  openOverpanelCount?: number,
  hideCloseButton?: boolean,
  children: any,
  zIndex: any,
}

export class Overpanel extends React.Component<OverpanelProps> {
  static defaultProps = {
    styleOverrides: {},
    shouldCloseOnEsc: true,
    hideCloseButton: false,
    preset: 'GLIMPSE',
  }
  static setAppElement = _setAppElement

  duration = this.props.styleOverrides.animationDuration || DEFAULT_DURATION // default animation duration, in millisecond

  componentDidMount() {
    // if overpanel mounts as show, do stuff; otherwise do nothing
    if (this.props.show) {
      this.onShow()
    }
  }
  componentWillUnmount() {
    // usually the overpanel should unmount after it closes.
    // In case it's unmounted directly, we need to clear stuff
    if (this.props.show) {
      this.onClose()
    }
  }

  componentDidUpdate(prevProps: OverpanelProps) {
    const { show } = this.props
    if (show === prevProps.show) {
      return
    }
    if (show) {
      this.onShow()
    } else {
      this.onClose()
    }
  }

  onShow = () => {
    const {
      openOverpanelCount,
      onOverpanelOpen = () => {},
      shouldCloseOnEsc,
      dispatch,
    } = this.props
    if (openOverpanelCount === 0) {
      // if this is the first overpanel, freeze the main document
      freezeMainContent()
    }
    dispatch(OverpanelActions.addOpenOverpanel())
    if (shouldCloseOnEsc) {
      document.addEventListener('keyup', this.handleEscKey)
    }
    // Custom logic to use when overpanel opens
    onOverpanelOpen()
  }

  onClose = () => {
    const { openOverpanelCount, shouldCloseOnEsc, dispatch } = this.props
    if (openOverpanelCount === 1) {
      /* If this is the only overpanel left, unfreeze the main document
      Unfreeze after completing the slide down animation
      otherwise the animation goes weird */
      setTimeout(unfreezeMainContent, this.duration)
    }
    dispatch(OverpanelActions.subtractClosedOverpanel())
    if (shouldCloseOnEsc) {
      document.removeEventListener('keyup', this.handleEscKey)
    }
  }

  handleEscKey = (e: any) => {
    if (e.keyCode === 27) {
      // esc key code
      this.handleCloseOverpanel()
    }
  }

  handleCloseOverpanel = () => {
    const {
      handleClose = () => {},
      onClosed = () => {},
      pageName,
      pageGroup,
    } = this.props
    if (pageName && pageGroup) {
      // this fires for both clicking on close button and pressing Esc button
      trackLink('close', pageGroup, pageName, 'link')
    }
    handleClose()
    if (onClosed) {
      // do something after the animation is done
      setTimeout(onClosed, this.duration)
    }
  }

  render() {
    const {
      show,
      children,
      a11yTitle,
      preset = 'GLIMPSE',
      hideCloseButton,
      styleOverrides = {},
    } = this.props
    return (
      <Portal>
        <CSSTransitionGroup
          transitionName={{
            enter: `${fadeEnter}`,
            enterActive: `${fadeEnterActive}`,
            leave: `${fadeLeave}`,
            leaveActive: `${fadeLeaveActive}`,
          }}
          transitionEnterTimeout={this.duration}
          transitionLeaveTimeout={this.duration}
          {...styleOverrides.backgroundAnimation}
        >
          {/* key is required by CSSTransitionGroup */}
          {show && (
            <Background key="background" css={styleOverrides.background} />
          )}
        </CSSTransitionGroup>
        <CSSTransitionGroup
          transitionName={{
            enter: `${slideEnter}`,
            enterActive: `${slideEnterActive}`,
            leave: `${slideLeave}`,
            leaveActive: `${slideLeaveActive}`,
          }}
          transitionEnterTimeout={this.duration}
          transitionLeaveTimeout={this.duration}
          {...styleOverrides.contentAnimation}
        >
          {show && (
            // key is required by CSSTransitionGroup
            <Wrapper
              key="wrapper"
              style={{ zIndex: this.props.zIndex || 1050 }}
            >
              <Content
                preset={preset}
                css={styleOverrides.content}
                data-pools-iframe-key="overpanel"
                // data-pools-iframe-key is used by poolsnodeweb to size the funding options iframe
              >
                <FocusLock returnFocus={true}>
                  {/* lock tab within the overpanel */}
                  <Dialog
                    role="dialog"
                    aria-label={a11yTitle}
                    tabIndex="-1"
                    aria-modal={true}
                    data-autofocus
                  >
                    <CloseButton
                      data-autofocus // auto focus specified by react-focus-lock
                      onClick={this.handleCloseOverpanel}
                      css={styleOverrides.closeButton}
                      hideCloseButton={hideCloseButton}
                    />
                    {children}
                  </Dialog>
                </FocusLock>
              </Content>
            </Wrapper>
          )}
        </CSSTransitionGroup>
      </Portal>
    )
  }
}

// Parent props will be prioritized over redux-store props
const mergeProps = (stateProps, dispatchProps, ownProps) => {
  return { ...stateProps, ...dispatchProps, ...ownProps }
}

export default connect(
  ({ server, overpanels }) => ({
    openOverpanelCount: overpanels.openOverpanelCount,
    hideCloseButton: server.env === 'pisp' && getFlowType() === 'request',
  }),
  null,
  mergeProps
)(Overpanel)
